import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  SectionTeacherRow,
  TeacherWorkloadMode,
  TeachingTypeOption,
} from 'src/app/services/models/subjects/subject.model';
import { SharedModule } from 'src/shared/shared.module';
import {
  normalizeTeachingRangeSelection,
  toNullableString,
} from '../../domain/subject-management-create.utils';
import { SubjectManagementStatusBadgeComponent } from '../status-badge/subject-management-status-badge.component';

@Component({
  selector: 'app-section-editor',
  imports: [CommonModule, ReactiveFormsModule, SharedModule, SubjectManagementStatusBadgeComponent],
  templateUrl: './section-editor.component.html',
  styleUrl: './section-editor.component.scss',
})
export class SectionEditorComponent implements OnDestroy {
  @Input({ required: true }) section!: FormGroup;
  @Input({ required: true }) sectionIndex!: number;
  @Input() teachingTypeOptions: TeachingTypeOption[] = [];
  @Input() teachingDayOptions: string[] = [];
  @Input() timeOptions: string[] = [];
  @Input() teachers: SectionTeacherRow[] = [];
  @Input() workloadMode: TeacherWorkloadMode = 'shared';
  @Input() isProjectResearch = false;
  @Input() locked = false;
  @Input() submitted = false;
  @Input() statusLabel = '-';
  @Input() status: string | null = null;
  @Input() approvalReady = false;
  @Input() isSubmittingApproval = false;
  @Input() editable = true;
  @Input() removable = true;
  @Input() showStatus = false;
  @Input() teacherManagementEnabled = true;
  @Input() sectionFieldsEditable = true;
  @Input() workloadHoursEditable = true;
  @Input() existingTeachingTimesEditable = true;
  @Input() editableTeachingTimeStartIndex = 0;
  @Input() respectLockedTeachingTimes = true;
  @Input() canAddTeachingTime = true;
  @Input() usedTeachingTimeIndexes: number[] = [];
  @Input() projectTeacherEditability: boolean[] = [];
  @Input() collapsible = false;
  @Input() expanded = true;
  @Input() showTopStatusBadge = true;
  @Input() saveMode = false;

  @Output() requestRemove = new EventEmitter<void>();
  @Output() saveSection = new EventEmitter<void>();
  @Output() addTeachingTime = new EventEmitter<void>();
  @Output() removeTeachingTime = new EventEmitter<number>();
  @Output() manageTeachers = new EventEmitter<void>();
  @Output() editProjectTeacher = new EventEmitter<number>();
  @Output() removeProjectTeacher = new EventEmitter<number>();
  @Output() submitApproval = new EventEmitter<void>();
  @Output() expandedChange = new EventEmitter<boolean>();

  hasTeacherTableHorizontalOverflow = false;

  private teacherTableWrapElement?: HTMLElement;
  private teacherTableElement?: HTMLElement;
  private teacherTableResizeObserver?: ResizeObserver;
  private overflowMeasurementTimer?: ReturnType<typeof setTimeout>;

  get teachingTimes(): FormArray<FormGroup> {
    return this.section.get('teachingTimes') as FormArray<FormGroup>;
  }

  get showProjectTeacherActionColumn(): boolean {
    return this.isProjectResearch;
  }

  get showProjectTeacherActionButtons(): boolean {
    return this.editable && !this.submitted;
  }

  @ViewChild('teacherTableWrap')
  set teacherTableWrap(elementRef: ElementRef<HTMLElement> | undefined) {
    this.observeTeacherTable(elementRef?.nativeElement);
  }

  ngOnDestroy(): void {
    this.teacherTableResizeObserver?.disconnect();

    if (this.overflowMeasurementTimer) {
      clearTimeout(this.overflowMeasurementTimer);
    }
  }

  hasSectionError(controlName: string): boolean {
    const control = this.section.get(controlName);
    return control != null && control.invalid && control.touched;
  }

  getSectionError(): string {
    const control = this.section.get('section');

    if (control?.touched !== true) {
      return '';
    }

    if (control.hasError('required')) {
      return 'กรุณากรอกข้อมูล';
    }

    return control.hasError('duplicateSection') ? 'Section ซ้ำกัน' : '';
  }

  hasTeachingTimeError(time: FormGroup, controlName: string): boolean {
    const control = time.get(controlName);
    return control != null && control.invalid && control.touched;
  }

  getTeachingTimesError(): string {
    if (this.isProjectResearch) {
      return '';
    }

    if (this.teachingTimes.hasError('timeOrder')) {
      return 'เวลาเริ่มต้นต้องน้อยกว่าเวลาสิ้นสุด';
    }

    return this.teachingTimes.hasError('timeOverlap')
      ? 'วันเดียวกันห้ามมีช่วงเวลาสอนซ้อนกัน'
      : '';
  }

  isTeachingTimeEditable(timeIndex: number): boolean {
    if (!this.editable) {
      return false;
    }

    if (this.respectLockedTeachingTimes && this.locked) {
      return false;
    }

    return !this.isTeachingTimeUsed(timeIndex)
      && (this.existingTeachingTimesEditable || timeIndex >= this.editableTeachingTimeStartIndex);
  }

  isAddTeachingTimeDisabled(): boolean {
    return !this.canAddTeachingTime || !this.editable || (this.respectLockedTeachingTimes && this.locked);
  }

  canRemoveTeachingTime(timeIndex: number): boolean {
    return timeIndex > 0 && this.isTeachingTimeEditable(timeIndex);
  }

  isTeachingTimeUsed(timeIndex: number): boolean {
    return this.usedTeachingTimeIndexes.includes(timeIndex);
  }

  isProjectTeacherEditable(teacherIndex: number): boolean {
    return this.projectTeacherEditability[teacherIndex] !== false;
  }

  getPreviewValue(controlName: string): string {
    if (controlName === 'teachingType') {
      const teachingTypeId = Number(this.section.get('teachingType')?.value);
      return this.teachingTypeOptions.find((option) => option.id === teachingTypeId)?.label ?? '-';
    }

    const value = this.section.get(controlName)?.value;
    return value == null || value === '' ? '-' : String(value);
  }

  getTeachingTimePreviewLines(): string[] {
    const lines = this.teachingTimes.controls
      .map((time) => {
        const day = toNullableString(time.get('teachingDay')?.value);
        const start = toNullableString(time.get('startTime')?.value);
        const end = toNullableString(time.get('endTime')?.value);

        if (day == null) {
          return '';
        }

        return start != null && end != null ? `${day} ${start} - ${end}` : day;
      })
      .filter(Boolean);

    return lines.length > 0 ? lines : ['-'];
  }

  getTeachingRangeDisplayLines(teachingRange: string): string[] {
    const ranges = normalizeTeachingRangeSelection(teachingRange);
    return ranges.length > 0 ? ranges : ['-'];
  }

  getCollapsedTeachingTypeLabel(): string {
    return this.isProjectResearch ? '' : this.getPreviewValue('teachingType');
  }

  // Template-facing derived value is kept beside the related presentation helpers.
  // eslint-disable-next-line @typescript-eslint/member-ordering
  get workloadModeLabel(): string {
    return this.workloadMode === 'shared' ? 'แบ่งภาระงาน' : 'ไม่แบ่งภาระงาน';
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get previewMode(): boolean {
    return this.submitted || !this.editable;
  }

  onDigitsOnlyKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Home', 'End',
    ];

    if (
      allowedKeys.includes(event.key)
      || ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
    ) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onDigitsOnlyInput(event: Event, control: AbstractControl | null): void {
    const input = event.target as HTMLInputElement | null;

    if (input == null || control == null) {
      return;
    }

    const digitsOnly = input.value.replace(/\D/g, '');
    input.value = digitsOnly;

    if (control.value !== digitsOnly) {
      control.setValue(digitsOnly);
    }
  }

  onDecimalKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Home', 'End',
    ];

    if (
      allowedKeys.includes(event.key)
      || ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
    ) {
      return;
    }

    if (!/^[\d.]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onDecimalInput(event: Event, control: AbstractControl | null): void {
    const input = event.target as HTMLInputElement | null;

    if (input == null || control == null) {
      return;
    }

    const [integerPart = '', ...decimalParts] = input.value.replace(/[^\d.]/g, '').split('.');
    const decimalValue = decimalParts.length > 0 ? `.${decimalParts.join('')}` : '';
    const normalizedValue = integerPart === '' ? decimalParts.join('') : `${integerPart}${decimalValue}`;

    input.value = normalizedValue;

    if (control.value !== normalizedValue) {
      control.setValue(normalizedValue);
    }
  }

  onToggleExpanded(): void {
    this.expandedChange.emit(!this.expanded);
  }

  private observeTeacherTable(wrapper: HTMLElement | undefined): void {
    this.teacherTableResizeObserver?.disconnect();
    this.teacherTableWrapElement = wrapper;
    this.teacherTableElement = wrapper?.querySelector<HTMLElement>('.subject-management-create-table') ?? undefined;

    if (wrapper == null) {
      this.setTeacherTableHorizontalOverflow(false);
      return;
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.teacherTableResizeObserver ??= new ResizeObserver(() => this.scheduleOverflowMeasurement());
      this.teacherTableResizeObserver.observe(wrapper);

      if (this.teacherTableElement != null) {
        this.teacherTableResizeObserver.observe(this.teacherTableElement);
      }
    }

    this.scheduleOverflowMeasurement();
  }

  private scheduleOverflowMeasurement(): void {
    if (this.overflowMeasurementTimer) {
      clearTimeout(this.overflowMeasurementTimer);
    }

    this.overflowMeasurementTimer = setTimeout(() => {
      this.overflowMeasurementTimer = undefined;
      this.updateTeacherTableHorizontalOverflow();
    });
  }

  private updateTeacherTableHorizontalOverflow(): void {
    const wrapper = this.teacherTableWrapElement;

    if (wrapper == null || wrapper.clientWidth <= 0) {
      return;
    }

    this.setTeacherTableHorizontalOverflow(wrapper.scrollWidth > wrapper.clientWidth + 1);
  }

  private setTeacherTableHorizontalOverflow(hasOverflow: boolean): void {
    if (this.hasTeacherTableHorizontalOverflow !== hasOverflow) {
      this.hasTeacherTableHorizontalOverflow = hasOverflow;
    }
  }
}
